/**
 * AgencyApply.jsx — public "Become a Studios agency" application page.
 * Route: /studio/apply (no auth required).
 *
 * Writes a row to agency_invitations with status=pending_review.
 * Sid + Lalith get notified and approve from /studio/admin.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import { ArrowRight, Spinner, Buildings, Check, ArrowLeft, X } from '@phosphor-icons/react';
import { SECTORS } from '@/utils/sector_options';
import PhoneField, { isValidPhoneNumber } from '@/components/PhoneField';

const ACCENT = '#6366f1';

const SERVICE_OPTIONS = [
  'linkedin_ghostwriting', 'founder_content', 'brand_identity', 'investor_decks',
  'short_form_video', 'instagram_reels', 'blog_writing', 'newsletter',
  'press_release', 'web_design', 'performance_marketing', 'seo',
];

const AgencyApply = () => {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '', tagline: '', description: '', contact_email: '', contact_phone: '',
    pricing_display: '', why_us: '',
    service_tags: [],
    sector_focus: [],
    portfolio_links: ['', '', ''],
  });
  const [busy, setBusy] = useState(false);

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleArr = (k, v) => setForm((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter(x => x !== v) : [...p[k], v] }));
  const updLink = (i, v) => setForm((p) => { const a = [...p.portfolio_links]; a[i] = v; return { ...p, portfolio_links: a }; });

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name.trim() || !form.contact_email.trim() || !form.tagline.trim()) {
      toast.error('Name, contact email and tagline are required'); return;
    }
    if (form.contact_phone && !isValidPhoneNumber(form.contact_phone)) {
      toast.error('Please enter a valid phone number'); return;
    }
    setBusy(true);
    try {
      await api.post('/agencies/apply', {
        ...form,
        portfolio_links: form.portfolio_links.filter(l => l.trim()),
      });
      setSubmitted(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
    finally { setBusy(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="studio-apply-success">
        <div className="max-w-md text-center">
          <div className="w-14 h-14 rounded-full mx-auto mb-5 flex items-center justify-center" style={{ backgroundColor: `${ACCENT}1a` }}>
            <Check size={26} weight="bold" style={{ color: ACCENT }} />
          </div>
          <h1 className="text-3xl font-heading font-bold text-text-primary mb-3" style={{ letterSpacing: '-0.02em' }}>Application received</h1>
          <p className="text-base text-text-secondary leading-relaxed mb-6">
            Thanks for applying. Our team will review and get back within 2 business days. If we want to move forward, we&apos;ll email you the onboarding link to claim your marketplace listing.
          </p>
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: ACCENT }}>
            Back to Founderboard <ArrowRight size={13} weight="bold" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="studio-apply-page">
      <div className="max-w-2xl mx-auto">
        <button onClick={() => navigate(-1)} className="text-xs text-text-muted hover:text-text-primary inline-flex items-center gap-1 mb-6">
          <ArrowLeft size={11} weight="bold" /> Back
        </button>
        <div className="flex items-center gap-2.5 mb-6">
          <span className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT}aa)` }}>
            <Buildings size={20} weight="fill" className="text-white" />
          </span>
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold" style={{ color: ACCENT }}>Founderboard Studios</div>
            <div className="text-base font-heading font-bold leading-none text-text-primary" style={{ letterSpacing: '-0.01em' }}>Become an agency</div>
          </div>
        </div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-text-primary leading-[1.05] mb-3" style={{ letterSpacing: '-0.02em' }}>
          List your agency on Founderboard Studios
        </h1>
        <p className="text-base text-text-secondary leading-relaxed mb-8 max-w-xl">
          Tell us about your shop. We hand-review every application before approving — quality bar matters more than headcount.
          Approved agencies get listed on the marketplace within 2 business days.
        </p>

        <form onSubmit={submit} className="space-y-5" data-testid="studio-apply-form">
          <Field label="Agency name *" testid="agency-apply-name">
            <input required type="text" value={form.name} onChange={(e) => upd('name', e.target.value)} placeholder="e.g. Brave Studios" data-testid="agency-apply-name" className="w-full bg-surface border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-text-primary" />
          </Field>
          <Field label="One-line tagline *">
            <input required type="text" value={form.tagline} onChange={(e) => upd('tagline', e.target.value)} placeholder="e.g. The studio behind India's most beloved D2C brands." maxLength={200} data-testid="agency-apply-tagline" className="w-full bg-surface border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-text-primary" />
          </Field>
          <Field label="About the agency">
            <textarea value={form.description} onChange={(e) => upd('description', e.target.value)} placeholder="A few paragraphs on team, philosophy, who you're best for." rows={4} maxLength={2000} data-testid="agency-apply-description" className="w-full bg-surface border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-text-primary" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Contact email *">
              <input required type="email" value={form.contact_email} onChange={(e) => upd('contact_email', e.target.value)} placeholder="founder@yourshop.com" data-testid="agency-apply-email" className="w-full bg-surface border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-text-primary" />
            </Field>
            <Field label="Phone (optional)">
              <PhoneField value={form.contact_phone} onChange={(v) => upd('contact_phone', v)} placeholder="98xxx xxxxx" data-testid="agency-apply-phone" />
            </Field>
          </div>
          <Field label="Pricing band you're comfortable advertising">
            <input type="text" value={form.pricing_display} onChange={(e) => upd('pricing_display', e.target.value)} placeholder="e.g. ₹50k – ₹2L /month" data-testid="agency-apply-pricing" className="w-full bg-surface border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-text-primary" />
          </Field>
          <Field label="Services you offer">
            <div className="flex flex-wrap gap-2" data-testid="agency-apply-services">
              {SERVICE_OPTIONS.map((s) => {
                const on = form.service_tags.includes(s);
                return (
                  <button key={s} type="button" onClick={() => toggleArr('service_tags', s)} data-testid={`agency-apply-service-${s}`} className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${on ? 'text-white' : 'text-text-secondary border-border hover:border-text-primary'}`} style={on ? { backgroundColor: ACCENT, borderColor: ACCENT } : {}}>
                    {s.replace(/_/g, ' ')}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Sectors you focus on">
            <div className="flex flex-wrap gap-2" data-testid="agency-apply-sectors">
              {SECTORS.map((s) => {
                const on = form.sector_focus.includes(s);
                return (
                  <button key={s} type="button" onClick={() => toggleArr('sector_focus', s)} data-testid={`agency-apply-sector-${s}`} className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${on ? 'text-white' : 'text-text-secondary border-border hover:border-text-primary'}`} style={on ? { backgroundColor: ACCENT, borderColor: ACCENT } : {}}>
                    {s}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Up to 3 portfolio links">
            <div className="space-y-2">
              {form.portfolio_links.map((l, i) => (
                <input key={i} type="url" value={l} onChange={(e) => updLink(i, e.target.value)} placeholder="https://…" data-testid={`agency-apply-portfolio-${i}`} className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-text-primary" />
              ))}
            </div>
          </Field>
          <Field label="Why should we add you?">
            <textarea value={form.why_us} onChange={(e) => upd('why_us', e.target.value)} placeholder="What makes you a great fit for early-stage Indian founders?" rows={3} maxLength={1000} data-testid="agency-apply-why" className="w-full bg-surface border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-text-primary" />
          </Field>
          <button type="submit" disabled={busy} data-testid="agency-apply-submit" className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold text-white disabled:opacity-50 transition-opacity hover:opacity-90" style={{ backgroundColor: ACCENT }}>
            {busy ? <Spinner size={14} className="animate-spin" /> : <Check size={14} weight="bold" />} Submit application
          </button>
          <p className="text-[10px] text-text-muted text-center">By submitting you agree we may contact you about your application.</p>
        </form>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">{label}</label>
    {children}
  </div>
);

export default AgencyApply;
